import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MDBBtn, MDBIcon, MDBInput } from 'mdb-react-ui-kit';
import { BOOTH_STATUS, boothKeyOf, searchBooths } from '../../../../utils/boothMap';
import { transformMoney } from '../../../../utils/money';
import { formatArea, getStatusLabel, positionTypeLabel, zoneLabel } from './boothStatusText';

const formatAreaM2 = (value) => `${formatArea(value)} m²`;

const sameKey = (a, b) => String(a).trim().toLowerCase() === String(b).trim().toLowerCase();

/**
 * One chip group of the filter bar ("offer first"): toggles `options[].key` in the list `selected`
 * (several chips of a group = OR). onChange(nextList).
 */
function ChipFilter({ id, label, options, selected, labelOf, onChange }) {
  const list = Array.isArray(selected) ? selected : [];
  const toggle = (key) => onChange(list.some((s) => sameKey(s, key)) ? list.filter((s) => !sameKey(s, key)) : [...list, key]);
  return (
    <div className="booth-filter-bar__chips" role="group" aria-labelledby={`${id}-label`} data-testid={id}>
      <div id={`${id}-label`} className="small fw-bold mb-1">
        {label}
      </div>
      <div className="d-flex flex-wrap gap-1">
        {options.map((option) => {
          const active = list.some((s) => sameKey(s, option.key));
          return (
            <MDBBtn
              key={option.key}
              type="button"
              size="sm"
              rounded
              color={active ? 'primary' : 'light'}
              className="text-none px-2 py-1 shadow-0"
              aria-pressed={active}
              onClick={() => toggle(option.key)}
            >
              {labelOf(option.key)}
              {option.count !== null && option.count !== undefined ? <span className="ms-1 opacity-75">({option.count})</span> : null}
            </MDBBtn>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Two-thumb range slider (two overlaid native range inputs). `range` is the real span of the booths
 * (boothMapView.valueSpan); the native inputs run on that span widened to whole `step`s so the thumbs
 * keep snapping to round values, while the labels are clamped to the real bounds.
 * `valueMin` / `valueMax` null = open end; onChange({ min, max }) reports a thumb at (or beyond) the
 * real end of the range as null so the filter stays open when the range grows (polling) and booths
 * without a value are not cut off.
 */
function RangeFilter({ id, label, range, step, valueMin, valueMax, format, onChange }) {
  const { t } = useTranslation();
  const sliderMin = Math.floor(range.min / step) * step;
  const sliderMax = Math.ceil(range.max / step) * step;
  const low = valueMin === null || valueMin === undefined ? sliderMin : Math.min(Math.max(Number(valueMin), sliderMin), sliderMax);
  const high = valueMax === null || valueMax === undefined ? sliderMax : Math.max(Math.min(Number(valueMax), sliderMax), sliderMin);
  const span = sliderMax - sliderMin;
  const percent = (value) => (span > 0 ? ((value - sliderMin) / span) * 100 : 0);
  // labels never leave the real span of the booths
  const clampToRange = (value) => Math.min(Math.max(value, range.min), range.max);

  const report = (nextLow, nextHigh) => {
    // a thumb in the widened part of the slider means the real end (both thumbs at 37.000 = from 36.800)
    const lowValue = clampToRange(nextLow);
    const highValue = clampToRange(nextHigh);
    onChange({ min: lowValue <= range.min ? null : lowValue, max: highValue >= range.max ? null : highValue });
  };

  return (
    <div className="booth-filter-bar__range">
      <div className="d-flex justify-content-between gap-2 small mb-1">
        <label htmlFor={`${id}-min`} className="fw-bold mb-0">
          {label}
        </label>
        <span className="text-muted text-nowrap">
          {format(clampToRange(low))} – {format(clampToRange(high))}
        </span>
      </div>
      <div className="booth-range">
        <div className="booth-range__track">
          <div className="booth-range__fill bg-primary" style={{ left: `${percent(low)}%`, right: `${100 - percent(high)}%` }} />
        </div>
        <input
          id={`${id}-min`}
          type="range"
          className="form-range booth-range__input"
          aria-label={t('{{label}} from', { label })}
          min={sliderMin}
          max={sliderMax}
          step={step}
          value={low}
          // thumbs on top of each other at the right end: the "from" thumb must stay draggable
          style={percent(low) > 50 ? { zIndex: 1 } : undefined}
          onChange={(e) => report(Math.min(Number(e.target.value), high), high)}
        />
        <input
          id={`${id}-max`}
          type="range"
          className="form-range booth-range__input"
          aria-label={t('{{label}} to', { label })}
          min={sliderMin}
          max={sliderMax}
          step={step}
          value={high}
          onChange={(e) => report(low, Math.max(Number(e.target.value), low))}
        />
      </div>
    </div>
  );
}

/**
 * One-row filter bar above the hall plan: search (booth number / exhibitor), price and size range
 * sliders, chips for position type / zone / offer (only the groups in `filterOptions`, see
 * boothMapView.filterOptionsOf: a group appears only when the plan has more than one value), reset.
 * Availability is not filtered; the hall plan legend shows the status colours.
 * The chips write filters.positionTypes / zones / offerCodes (boothMap.boothMatchesFilters).
 */
export default function BoothFilters({ booths, filters, onChange, onReset, priceRange, areaRange, filterOptions, onPickSearchHit, bookingOpen = true }) {
  const { t } = useTranslation();
  const prices = priceRange || { min: 0, max: 0 };
  const areas = areaRange || { min: 0, max: 0 };
  const options = filterOptions || {};
  const positionOptions = Array.isArray(options.positionTypes) ? options.positionTypes : [];
  const zoneOptions = Array.isArray(options.zones) ? options.zones : [];
  const offerOptions = Array.isArray(options.offerCodes) ? options.offerCodes : [];

  const hits = useMemo(() => searchBooths(booths, filters.query), [booths, filters.query]);

  const update = (patch) => onChange({ ...filters, ...patch });

  return (
    <div className="booth-filter-bar booth-filter-bar__row">
      <div className="booth-filter-bar__search position-relative">
        <MDBInput
          label={t('Exhibitor or booth number')}
          value={filters.query}
          onChange={(e) => update({ query: e.target.value })}
          type="search"
        />
        {/* overlays the hall plan instead of pushing it down */}
        {hits.length > 0 && (
          <div className="list-group shadow-2-strong booth-filter-bar__hits">
            {hits.map((booth) => (
              <button
                key={boothKeyOf(booth)}
                type="button"
                className="list-group-item list-group-item-action d-flex justify-content-between align-items-center py-2 px-3"
                onClick={() => onPickSearchHit && onPickSearchHit(booth)}
              >
                <span className="fw-bold">{booth.boothNumber}</span>
                <span className="small text-muted text-end ms-2">
                  {booth.status === BOOTH_STATUS.SOLD && booth.exhibitor && booth.exhibitor.name
                    ? booth.exhibitor.name
                    : getStatusLabel(t, booth.status, bookingOpen)}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
      {prices.max > prices.min && (
        <RangeFilter
          id="booth-filter-price"
          label={t('Booth price')}
          range={prices}
          step={500}
          valueMin={filters.priceMin}
          valueMax={filters.priceMax}
          format={transformMoney}
          onChange={({ min, max }) => update({ priceMin: min, priceMax: max })}
        />
      )}
      {areas.max > areas.min && (
        <RangeFilter
          id="booth-filter-area"
          label={t('Booth size')}
          range={areas}
          step={1}
          valueMin={filters.areaMin}
          valueMax={filters.areaMax}
          format={formatAreaM2}
          onChange={({ min, max }) => update({ areaMin: min, areaMax: max })}
        />
      )}
      {positionOptions.length > 1 && (
        <ChipFilter
          id="booth-filter-position"
          label={t('Position type')}
          options={positionOptions}
          selected={filters.positionTypes}
          labelOf={(key) => positionTypeLabel(t, key)}
          onChange={(positionTypes) => update({ positionTypes })}
        />
      )}
      {zoneOptions.length > 1 && (
        <ChipFilter
          id="booth-filter-zone"
          label={t('Zone')}
          options={zoneOptions}
          selected={filters.zones}
          labelOf={(key) => zoneLabel(t, key)}
          onChange={(zones) => update({ zones })}
        />
      )}
      {offerOptions.length > 1 && (
        <ChipFilter
          id="booth-filter-offer"
          label={t('Offer')}
          options={offerOptions}
          selected={filters.offerCodes}
          labelOf={(key) => key}
          onChange={(offerCodes) => update({ offerCodes })}
        />
      )}
      <MDBBtn color="link" size="sm" className="text-none px-1 text-nowrap ms-auto" onClick={onReset}>
        <MDBIcon fas icon="undo" className="me-1" />
        {t('Reset filters')}
      </MDBBtn>
    </div>
  );
}
